import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/content";
import { Reveal } from "@/components/site/reveal";

const shots = [
  {
    src: IMAGES.work1,
    label: "Turnaround Cleans",
    caption: "Hotel-standard changeovers that keep guests reviewing five stars.",
    aspect: "aspect-[4/5]",
  },
  {
    src: IMAGES.work2,
    label: "Specialist Deep Cleaning",
    caption: "Carpets, upholstery & end-of-season resets by our in-house teams.",
    aspect: "aspect-[4/5]",
  },
];

export default function Showcase() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 40]);

  return (
    <section id="work" ref={ref} className="bg-bone py-24 md:py-32" data-testid="showcase-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal><p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">The Work</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif text-4xl leading-tight text-navy sm:text-5xl md:text-6xl">
                Performance you<br />can see.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base leading-relaxed text-navy/60">
              Every property is cleaned, maintained and presented by our own uniformed
              specialists &mdash; documented with photo reports after each visit.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 md:gap-10">
          {shots.map((s, i) => (
            <motion.figure
              key={s.label}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative ${i === 1 ? "md:mt-16" : ""}`}
              data-testid={`showcase-item-${i}`}
            >
              <div className={`relative overflow-hidden ${s.aspect} border border-navy/10`}>
                <motion.img
                  style={{ y: i === 0 ? y1 : y2 }}
                  src={s.src}
                  alt={s.label}
                  className="h-[112%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold">{s.label}</p>
                  <p className="mt-2 max-w-xs font-serif text-xl text-white">{s.caption}</p>
                </div>
              </div>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
